import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useRole } from "../../components/AppShell";
import { BackIcon, PlusIcon, WhatsAppIcon } from "../../components/icons";
import { Avatar, ListSkeleton, useConfirm, useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { formatPhone, shortTime } from "../../lib/format";
import {
  type QuoteInput,
  useDeleteQuote,
  useMe,
  useProducts,
  useQuote,
  useSendQuote,
  useTeam,
  useUpdateQuote,
} from "../../lib/queries";
import type { QuoteCard, QuoteLine } from "../../lib/types";
import {
  lineFromProduct,
  lineTotal,
  money,
  quoteNumber,
  quoteTotals,
  STATUS_LABEL,
  STATUS_PILL,
  shortDate,
  shownStatus,
} from "./quote-text";

export function QuotePage() {
  const { quoteId } = useParams({ from: "/app/cotizaciones/$quoteId" });
  const q = useQuote(quoteId);
  if (q.isError)
    return (
      <div className="page">
        <BackLink />
        <p className="muted">Esta cotización ya no existe.</p>
      </div>
    );
  if (!q.data)
    return (
      <div className="page">
        <BackLink />
        <div className="card">
          <ListSkeleton rows={3} />
        </div>
      </div>
    );
  // A new key when it changes elsewhere (another tab, a teammate): the draft starts over from it.
  return <QuoteEditor key={`${q.data.id}:${q.data.updatedAt}`} q={q.data} />;
}

function BackLink() {
  return (
    <Link className="back-link" to="/cotizaciones">
      <BackIcon />
      Cotizaciones
    </Link>
  );
}

type Draft = Pick<QuoteCard, "currency" | "terms" | "reference" | "validUntil"> & { lines: QuoteLine[] };

const draftOf = (q: QuoteCard): Draft => ({
  currency: q.currency,
  terms: q.terms,
  reference: q.reference,
  validUntil: q.validUntil,
  lines: q.lines.map(({ productId, description, model, quantity, unitPrice, discount }) => ({
    productId,
    description,
    model,
    quantity,
    unitPrice,
    discount,
  })),
});

const num = (v: string, fallback = 0) => (v.trim() === "" || Number.isNaN(Number(v)) ? fallback : Number(v));

function QuoteEditor({ q }: { q: QuoteCard }) {
  const role = useRole();
  const me = useMe().data;
  const team = useTeam().data?.items ?? [];
  const catalog = useProducts("", "").data?.items ?? [];
  const update = useUpdateQuote(q.id);
  const send = useSendQuote(q.id);
  const [sendError, setSendError] = useState<string | null>(null);
  const remove = useDeleteQuote();
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();
  const [d, setD] = useState<Draft>(() => draftOf(q));
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((prev) => ({ ...prev, [k]: v }));
  const setLine = (i: number, patch: Partial<QuoteLine>) =>
    set(
      "lines",
      d.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)),
    );
  const dirty = JSON.stringify(d) !== JSON.stringify(draftOf(q));
  const draft = q.status === "draft";
  const status = shownStatus(q);
  const totals = quoteTotals(d.lines, q.pricesIncludeTax, q.taxRate);
  const valid = d.lines.every((l) => l.description.trim() && l.quantity > 0);
  const manager = role === "owner" || role === "admin";
  const assignable = manager ? team : team.filter((m) => m.id === me?.user.id || m.id === q.assigneeId);

  const input = (): QuoteInput => ({
    ...(draft
      ? { currency: d.currency, lines: d.lines.map((l) => ({ ...l, description: l.description.trim() })) }
      : {}),
    terms: d.terms,
    reference: d.reference,
    validUntil: d.validUntil,
  });

  async function save(then?: QuoteInput, done = "Cotización guardada") {
    try {
      if (dirty) await update.mutateAsync(input());
      if (then) await update.mutateAsync(then);
      toast(done);
      return true;
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudo guardar.");
      return false;
    }
  }

  async function openPdf(download = false) {
    if (dirty && !(await save(undefined, "Guardada"))) return;
    window.open(`/api/quotes/${q.id}/pdf${download ? "?descargar=1" : ""}`, "_blank", "noopener");
  }

  async function sendByWhatsApp() {
    setSendError(null);
    if (dirty && !(await save(undefined, "Guardada"))) return;
    try {
      await send.mutateAsync();
      toast("Cotización enviada por WhatsApp");
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      setSendError(
        code === "window_closed"
          ? "Pasaron más de 24 horas desde el último mensaje del cliente: WhatsApp solo deja escribirle con una plantilla aprobada. Escríbele con una plantilla y, cuando responda, envía la cotización."
          : code === "not_found"
            ? "Este cliente todavía no tiene una conversación de WhatsApp. Descarga el PDF y envíalo por otro medio."
            : code === "forbidden"
              ? "La conversación de este cliente la atiende otra persona."
              : "No se pudo enviar por WhatsApp. Intenta de nuevo o descarga el PDF.",
      );
    }
  }

  async function onDelete() {
    const ok = await confirm({
      title: `¿Eliminar la cotización ${quoteNumber(q.number)}?`,
      message: "Se borra con sus líneas y no se puede deshacer.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    await remove.mutateAsync(q.id);
    toast("Cotización eliminada");
    navigate({ to: "/cotizaciones" });
  }

  return (
    <div className="page">
      <BackLink />
      <div className="card case-hero">
        <div className="case-hero-main">
          <span className="case-number mono">
            {quoteNumber(q.number)}
            {q.reference && <span className="faint"> · {q.reference}</span>}
          </span>
          <h1>{money(totals.total, d.currency)}</h1>
          <span className="contact-hero-tags">
            <span className={`pill ${STATUS_PILL[status]}`}>{STATUS_LABEL[status]}</span>
            <span className="tag">{q.pricesIncludeTax ? "IVA incluido" : `+ IVA ${q.taxRate} %`}</span>
            <span className="faint">
              {q.sentAt ? `Enviada ${shortTime(q.sentAt)}` : `Creada ${shortTime(q.createdAt)}`}
            </span>
          </span>
        </div>
        <Link className="case-customer" to="/contactos/$contactId" params={{ contactId: q.contact.id }}>
          <Avatar name={q.contact.name} fallback={q.contact.waId} />
          <span className="entity-main">
            <span className="entity-name">{q.contact.name ?? "Sin nombre"}</span>
            <span className="entity-meta mono">{formatPhone(q.contact.waId)}</span>
          </span>
        </Link>
        <div className="case-controls">
          <div className="field">
            <label htmlFor="quote-valid">Válida hasta</label>
            <input
              className="input"
              id="quote-valid"
              type="date"
              value={d.validUntil}
              onChange={(e) => e.target.value && set("validUntil", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="quote-assignee">Responsable</label>
            <select
              className="select"
              id="quote-assignee"
              value={q.assigneeId ?? ""}
              disabled={update.isPending}
              onChange={(e) =>
                update
                  .mutateAsync({ assigneeId: e.target.value || null })
                  .catch((err) =>
                    toast(
                      err instanceof ApiError && err.status === 403
                        ? "Solo puedes tomar la cotización para ti."
                        : "No se pudo asignar.",
                    ),
                  )
              }
            >
              <option value="">Sin responsable</option>
              {assignable.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.id === me?.user.id ? `${m.name} (yo)` : m.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {!draft && (
        <div className="banner quote-locked">
          <span>
            {q.status === "sent"
              ? `Se envió${q.expired ? ` y venció el ${shortDate(q.validUntil)}` : ""}. Las líneas no cambian para que coincidan con lo que tiene el cliente.`
              : `${STATUS_LABEL[q.status]}. Las líneas quedan como se enviaron.`}
          </span>
          <button
            className="btn btn-sm"
            type="button"
            disabled={update.isPending}
            onClick={() => save({ status: "draft" }, "Volvió a borrador")}
          >
            Volver a borrador
          </button>
        </div>
      )}

      <section className="card" aria-labelledby="quote-lines-title">
        <div className="card-body">
          <div className="quote-lines-head">
            <h3 id="quote-lines-title">Líneas</h3>
            {draft && (
              <select
                className="select quote-currency"
                aria-label="Moneda"
                value={d.currency}
                onChange={(e) => set("currency", e.target.value as Draft["currency"])}
              >
                <option value="CRC">Colones (₡)</option>
                <option value="USD">Dólares (US$)</option>
              </select>
            )}
          </div>
          {d.lines.length > 0 && (
            <div className="quote-lines">
              <div className="quote-line quote-line-head">
                <span>Descripción</span>
                <span>Cant.</span>
                <span>Precio unit.</span>
                <span>Desc. %</span>
                <span>Total</span>
                <span />
              </div>
              {d.lines.map((l, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: lines are edited in place
                <div className="quote-line" key={i}>
                  <span className="quote-line-desc">
                    <input
                      className="input"
                      aria-label={`Descripción de la línea ${i + 1}`}
                      placeholder="Ej.: Silla plástica apilable"
                      maxLength={500}
                      disabled={!draft}
                      value={l.description}
                      onChange={(e) => setLine(i, { description: e.target.value })}
                    />
                    {l.model && <span className="faint mono">{l.model}</span>}
                  </span>
                  <label className="quote-cell">
                    <span className="quote-cell-label">Cant.</span>
                    <input
                      className="input"
                      type="number"
                      min={0}
                      step="any"
                      aria-label={`Cantidad de la línea ${i + 1}`}
                      placeholder="Ej.: 1"
                      disabled={!draft}
                      value={l.quantity}
                      onChange={(e) => setLine(i, { quantity: num(e.target.value) })}
                    />
                  </label>
                  <label className="quote-cell">
                    <span className="quote-cell-label">Precio unit.</span>
                    <input
                      className="input"
                      type="number"
                      min={0}
                      step="any"
                      aria-label={`Precio unitario de la línea ${i + 1}`}
                      placeholder="Ej.: 25000"
                      disabled={!draft}
                      value={l.unitPrice}
                      onChange={(e) => setLine(i, { unitPrice: num(e.target.value) })}
                    />
                  </label>
                  <label className="quote-cell">
                    <span className="quote-cell-label">Desc. %</span>
                    <input
                      className="input"
                      type="number"
                      min={0}
                      max={100}
                      step="any"
                      aria-label={`Descuento de la línea ${i + 1}`}
                      placeholder="Ej.: 10"
                      disabled={!draft}
                      value={l.discount}
                      onChange={(e) => setLine(i, { discount: Math.min(100, num(e.target.value)) })}
                    />
                  </label>
                  <span className="quote-line-total mono">{money(lineTotal(l), d.currency)}</span>
                  {draft ? (
                    <button
                      className="btn btn-sm btn-ghost"
                      type="button"
                      aria-label={`Quitar la línea ${i + 1}`}
                      onClick={() =>
                        set(
                          "lines",
                          d.lines.filter((_, j) => j !== i),
                        )
                      }
                    >
                      Quitar
                    </button>
                  ) : (
                    <span />
                  )}
                </div>
              ))}
            </div>
          )}
          {draft && (
            <div className="quote-add">
              <select
                className="select"
                aria-label="Agregar del catálogo"
                value=""
                onChange={(e) => {
                  const p = catalog.find((x) => x.id === e.target.value);
                  if (p) set("lines", [...d.lines, lineFromProduct(p, q.contact.type)]);
                }}
              >
                <option value="">Agregar del catálogo…</option>
                {catalog.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.model ? `${p.model} · ${p.name}` : p.name}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-sm"
                type="button"
                onClick={() =>
                  set("lines", [
                    ...d.lines,
                    { productId: null, description: "", model: null, quantity: 1, unitPrice: 0, discount: 0 },
                  ])
                }
              >
                <PlusIcon />
                Línea libre
              </button>
            </div>
          )}
          {d.lines.length === 0 && (
            <p className="muted">Agrega productos del catálogo o escribe una línea libre (un trabajo, un envío).</p>
          )}

          <dl className="quote-totals">
            {totals.discount > 0 && (
              <>
                <dt>Descuentos aplicados</dt>
                <dd className="mono">-{money(totals.discount, d.currency)}</dd>
              </>
            )}
            <dt>{q.pricesIncludeTax ? "Subtotal sin IVA" : "Subtotal"}</dt>
            <dd className="mono">{money(totals.subtotal, d.currency)}</dd>
            <dt>IVA ({q.taxRate} %)</dt>
            <dd className="mono">{money(totals.tax, d.currency)}</dd>
            <dt className="quote-grand">Total</dt>
            <dd className="quote-grand mono">{money(totals.total, d.currency)}</dd>
          </dl>
        </div>
      </section>

      <section className="card" aria-labelledby="quote-terms-title">
        <div className="card-body">
          <h3 id="quote-terms-title">Condiciones</h3>
          <textarea
            className="textarea"
            rows={3}
            aria-label="Condiciones"
            placeholder="Ej.: Precios en colones. Pago contra entrega. Entrega en 3 días hábiles en el GAM."
            maxLength={2000}
            value={d.terms ?? ""}
            onChange={(e) => set("terms", e.target.value)}
          />
          <div className="field">
            <label htmlFor="quote-reference">Referencia interna (opcional)</label>
            <input
              className="input mono"
              id="quote-reference"
              placeholder="Ej.: Pedido 2219 del ERP"
              maxLength={120}
              value={d.reference ?? ""}
              onChange={(e) => set("reference", e.target.value)}
            />
          </div>
        </div>
      </section>

      {sendError && <div className="banner banner-warn">{sendError}</div>}

      <div className="product-save quote-actions">
        {manager && (
          <button className="btn btn-danger" type="button" onClick={onDelete}>
            Eliminar
          </button>
        )}
        <button className="btn" type="button" disabled={!valid || update.isPending} onClick={() => openPdf()}>
          Ver PDF
        </button>
        <button className="btn" type="button" disabled={!valid || update.isPending} onClick={() => openPdf(true)}>
          Descargar
        </button>
        {dirty && (
          <button className="btn" type="button" disabled={!valid || update.isPending} onClick={() => save()}>
            {update.isPending ? "Guardando…" : "Guardar"}
          </button>
        )}
        {q.status === "draft" && (
          <>
            <button
              className="btn"
              type="button"
              title="Si la enviaste por otro medio (correo, en persona)"
              disabled={!valid || !d.lines.length || update.isPending}
              onClick={() => save({ status: "sent" }, "Marcada como enviada")}
            >
              Marcar como enviada
            </button>
            <button
              className="btn btn-primary"
              type="button"
              disabled={!valid || !d.lines.length || update.isPending || send.isPending}
              onClick={sendByWhatsApp}
            >
              <WhatsAppIcon />
              {send.isPending ? "Enviando…" : "Enviar por WhatsApp"}
            </button>
          </>
        )}
        {q.status === "sent" && (
          <>
            <button
              className="btn"
              type="button"
              disabled={update.isPending}
              onClick={() => save({ status: "rejected" }, "Marcada como rechazada")}
            >
              Rechazada
            </button>
            <button
              className="btn btn-primary"
              type="button"
              disabled={update.isPending}
              onClick={() => save({ status: "accepted" }, "¡Cotización aceptada!")}
            >
              Aceptada
            </button>
          </>
        )}
      </div>
    </div>
  );
}
